import { useCallback } from 'react';

import { useSubmitVacancyInteractionMutation } from 'core/graphql';

/** The two interaction kinds the feed can record (spec 0018 §B2). */
export type VacancyInteractionKind = 'saved' | 'applied';

export interface IMarkVacancyInteractionResult {
  success: boolean;
  /** Backend error code, if any (e.g. `not_a_member`, `not_found`). */
  errorCode: string | null;
  /** Server-worded message; the page falls back to a translated string. */
  errorMessage: string | null;
}

/**
 * Records a Save / Apply interaction on a vacancy.
 *
 * No refetch: the mutation returns `vacancy { id myInteraction }`, which
 * updates the normalized `VacancyType:id` entity in place, so the feed row
 * reflects the new badge/button state immediately (task: response-update is
 * fine). Repeat taps are safe — the backend's unique (member, vacancy, kind)
 * row is the double-submit guard.
 *
 * The action is one-way (no un-save / un-apply); the page disables a button
 * once its state is reached rather than toggling it.
 *
 * A network error propagates as a thrown ApolloError — callers own the retry UX.
 */
export const useMarkVacancyInteraction = () => {
  const [mutate, { loading }] = useSubmitVacancyInteractionMutation();

  const markInteraction = useCallback(
    async (
      vacancyId: string,
      kind: VacancyInteractionKind,
    ): Promise<IMarkVacancyInteractionResult> => {
      const { data } = await mutate({ variables: { vacancyId, kind } });

      const payload = data?.markVacancyInteraction;
      const firstError = payload?.customErrors?.[0] ?? null;

      return {
        success: Boolean(payload?.success),
        errorCode: firstError?.code ?? null,
        errorMessage: firstError?.message ?? null,
      };
    },
    [mutate],
  );

  return { markInteraction, loading };
};
